import { BACKEND_KEYS } from '../../common/consts/app-keys.const';
import {
  EnhancedWithAuthHttpService,
  HttpFactoryService
} from '../../common/services';

export type StandbyCard = {
  id: string;
  eyebrow: string;
  heading: string;
  body: string;
  chips: string[];
  /** Inline SVG markup, authored server-side. Drawn in currentColor. */
  art: string;
  wide?: boolean;
};

export type StandbyPage = {
  hero: { eyebrow: string; wordmark: string; tagline: string; scrollHint: string };
  intro: string;
  cards: StandbyCard[];
  cta: { heading: string; body: string; label: string; href: string };
  video: {
    token: string;
    variants: string[];
    /**
     * The opening frame of each cut, inlined as a data URI keyed
     * `${theme}-${landscape|vertical}`. Sent with the page rather than fetched
     * so there is no second round trip before anything is on screen, and it is
     * the *first* frame specifically — a poster from further into the film
     * shows a moment the video then jumps away from.
     */
    firstFrames: Record<string, string>;
    /** What each cut opens on, so the stage behind it is never a black plate. */
    stageBg: Record<'light' | 'dark', string>;
  };
};

const API_BASE =
  process.env.REACT_APP_BUILD_ENV === 'development'
    ? `${process.env.REACT_APP_BACKEND_URL_DEV}/api/v1`
    : `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

class StandbyService {
  httpService: EnhancedWithAuthHttpService;

  constructor(private httpFactory: HttpFactoryService) {
    this.httpService = httpFactory.createAuthHttpService();
  }

  /**
   * The page in the caller's language. The copy is translated server-side, so
   * the language has to travel with the request — the API cannot read
   * react-i18next's state from here.
   */
  async getPage(lang?: string): Promise<StandbyPage | null> {
    const query = lang ? `?lang=${encodeURIComponent(lang)}` : '';
    const response: StandbyPage | void = await this.httpService.get(
      `${BACKEND_KEYS.STANDBY_PAGE}${query}`
    );
    return response ?? null;
  }

  /**
   * The version Standby is running. Answered by our own API, which asks
   * Standby and caches the answer — so the menu never waits on another
   * service, and never shows a number copied by hand.
   */
  async getVersion(): Promise<string | null> {
    try {
      const res = await fetch(`${API_BASE}/standby/version`);
      if (!res.ok) return null;
      return ((await res.json()) as { version?: string | null })?.version ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Media URLs carry the signed token from getPage rather than relying on the
   * auth cookie: a <video> tag will not send credentials cross-origin, and
   * Safari is unreliable about it for the range requests it needs to start
   * playback at all.
   */
  videoUrl(theme: 'light' | 'dark', variant: string, token: string): string {
    return `${API_BASE}/standby/video/${theme}/${variant}?t=${encodeURIComponent(token)}`;
  }

}

export const standbyService = new StandbyService(new HttpFactoryService());
